import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavBarCollapsableContext } from '../../../helpers/context'
import {
  calendar,
  copy,
  editIcon,
  emptyImage,
  goToIcon,
  points,
  scan,
  tag,
} from '../../../images'
import { useNavigate, useParams } from 'react-router-dom'
import DialogModal from '../../../components/dialogModal'
import EditLinksForm from '../../../components/forms/links/formEdit'
import Calendar from '../../../components/calendar'
import ByTimeDashboard from '../../../components/dashboard/byTime'
import { toast } from 'react-toastify'
import {
  formatDate,
  formatDateLong,
  getToken,
  getTokenExpiration,
  logOut,
} from '../../../helpers/utils'
import { AppDispatch, useAppDispatch } from '../../../redux/store'
import {
  editLink,
  getClicksPerDay,
  getLink,
  getLinkClicks,
} from '../../../redux/reducers/urlReducer'

interface utmValues {
  origin: string
  media: string
  campaign: string
  term: string
  content: string
}

interface EditFormValues {
  _id: string
  custom_code: string
  title: string
  original_url: string
  short_url: string
  tags: string[]
  domain: string
  utm: utmValues
}

/**Links is a view returns the components LinksCards with the urls created adn the modal for editing and deleting them */
const LinkDetail = () => {
  const { t } = useTranslation()
  const classContext = useContext(NavBarCollapsableContext)
  const [openEdit, setOpenEdit] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const dispatch: AppDispatch = useAppDispatch()
  const [chartData, setChartData] = useState([])
  const { id } = useParams()
  const token = getToken()
  const urlId = id || ''
  const currentDate = new Date()
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  )

  const [dateRange, setDateRange] = useState({
    startDate: firstDayOfMonth,
    endDate: currentDate,
  })
  const formattedFirstDayOfMonth = `${firstDayOfMonth.getFullYear()}-${(
    firstDayOfMonth.getMonth() + 1
  )
    .toString()
    .padStart(2, '0')}-01`
  const formattedDate = `${currentDate.getFullYear()}-${(
    currentDate.getMonth() + 1
  )
    .toString()
    .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`

  const [toDateSelected, setToDateSelected] = useState<string>(formattedDate)
  const [fromDateSelected, setFromDateSelected] = useState<string>(
    formattedFirstDayOfMonth
  )
  let navigate = useNavigate()
  const [dataLink, setDataLink] = useState({
    _id: '',
    original_url: '',
    short_url: '',
    title: '',
    domain: '',
    custom_code: '',
    tags: [],
    has_qr: false,
    utm: {
      origin: '',
      media: '',
      campaign: '',
      term: '',
      content: '',
    },
    status: 1,
    createdAt: '',
    tracking_count: 0,
  })

  const sessionExpired = getTokenExpiration()

  useEffect(() => {
    if (sessionExpired) logOut()
  }, [sessionExpired])

  useEffect(() => {
    if (urlId) {
      getData(urlId)
      getDataGraph()
    }
  }, [urlId])

  const handleEdit = () => {
    setOpenEdit(true)
    setShowMenu(false)
  }

  const getData = async (linkId: string) => {
    try {
      const { payload } = await dispatch(getLink({ token: token, id: linkId }))

      if (payload.success) {
        setDataLink(payload.data)
      } else {
        toast(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const goTo = (url: string, blank: boolean) => {
    if (blank) window.open(url)
    else window.location.href = url
  }

  const handleSubmitEdit = (values: EditFormValues) => {
    const body = {
      urlId: values._id,
      url: values.original_url,
      domain: values.domain,
      title: values.title,
      custom_code: values.custom_code,
      has_qr: false,
      utm: {
        origin: values.utm.origin,
        media: values.utm.media,
        campaign: values.utm.campaign,
        term: values.utm.term,
        content: values.utm.content,
      },
      tags: values.tags,
    }

    handleEditLink(body)
    setOpenEdit(false)
  }

  const handleEditLink = async (body: any) => {
    try {
      const { payload } = await dispatch(editLink({ token, body: body }))

      if (payload && payload.success) {
        toast(`${t('DATA_UPDATED')}`)
        getData(urlId)
      } else {
        toast(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url)
    toast(`${t('LINK_COPIED')}`)
  }

  const handleMenu = () => {
    setShowMenu(!showMenu)
  }

  const onChange = (ranges: any) => {
    setDateRange(ranges)
    setFromDateSelected(ranges.startDate)
    setToDateSelected(ranges.endDate)
  }

  const getDataGraph = async () => {
    try {
      const { payload } = await dispatch(
        getClicksPerDay({
          token: token,
          urlId: urlId,
          from: fromDateSelected,
          to: toDateSelected,
        })
      )

      if (payload) {
        setChartData(payload?.data)
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }
  const handleCloseEdit = () => {
    setOpenEdit(false)
  }

  const Menu = () => {
    return (
      <div className='container-option-extra-details'>
        <button
          className='button-option-extra'
          onClick={() => goTo(dataLink?.short_url, true)}
        >
          <img
            src={goToIcon}
            alt={`${t('GO_TO_LINK')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('GO_TO_LINK')}`}</span>
        </button>
        <hr></hr>
        <button className='button-option-extra' onClick={() => handleEdit()}>
          <img
            src={editIcon}
            alt={`${t('EDIT')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('EDIT')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => {
            copyLink(dataLink?.short_url)
          }}
        >
          <img src={copy} alt={`${t('COPY')}`} className='icon-option-extra' />
          <span className='option-extra'>{`${t('COPY')}`}</span>
        </button>
      </div>
    )
  }

  return (
    <>
      <div className={`container-home`}>
        <div className={classContext.classes.navbar_padding_left}>
          <div>
            <div className='container-link-card mx-2' key={dataLink?._id}>
              <div className='link-card'>
                <div className='p-4'>
                  <button
                    className='button-link'
                    onClick={() => navigate('/links')}
                  >
                    {`${t('GO_BACK_LINKS')}`}
                  </button>
                </div>

                <div className='link-card-icon-information'>
                  <div className='link-card-icon-container'>
                    <div className='link-card-icon-container'>
                      <img
                        alt={dataLink?.title}
                        src={`${dataLink?.original_url}/favicon.ico`}
                        className='img-card-link'
                        onError={(e) => {
                          const imgElement = e.target as HTMLImageElement
                          imgElement.src = emptyImage
                        }}
                      />
                    </div>
                  </div>
                  <div className='link-content-title'>
                    <div className='d-grid'>
                      <span>{`${t(dataLink?.title)}`}</span>
                      <a
                        className='link-card-long-url'
                        href={dataLink?.original_url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {dataLink?.original_url}
                      </a>
                      <a
                        id='short-url'
                        defaultValue={dataLink?.short_url}
                        className='link-card-short-url'
                        href={dataLink?.short_url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {dataLink?.short_url}
                      </a>
                    </div>
                  </div>
                  <div className='container-url-track'>
                    <div className='container-track-card'>
                      <div className='pt-4 pt-md-0'>
                        <img
                          className='button-card-icon'
                          src={scan}
                          alt={`${t('SCANS')}`}
                        />
                        <span>{dataLink?.tracking_count}</span>
                      </div>
                      <div className='py-4 py-md-0'>
                        <img
                          className='button-card-icon'
                          src={tag}
                          alt={`${t('TAG')}`}
                        />
                        {dataLink?.tags?.length > 0 ? (
                          dataLink?.tags?.map((elem: string, index: number) => (
                            <button key={elem} className='button-tags'>{`${t(
                              elem
                            )}`}</button>
                          ))
                        ) : (
                          <span>{`${t('NO_TAGS')}`}</span>
                        )}
                      </div>

                      <div>
                        <img
                          className='button-card-icon'
                          src={calendar}
                          alt={`${t('DATE')}`}
                        />
                        <span>{formatDate(dataLink.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className='container-button-menu mb-4'>
                    <button
                      className='button-general'
                      type='button'
                      onClick={() => handleMenu()}
                    >
                      <img
                        className='button-card-icon'
                        src={points}
                        alt={`${t('SHOW_MENU')}`}
                      />
                    </button>
                    {showMenu && <Menu />}
                  </div>
                </div>
              </div>
            </div>

            <div className='container-graphic-calendar'>
              <div className='container-calendar-graphic'>
                <div className='col-12 col-md-4 plan-card mb-md-0 mb-3  margin-right-2'>
                  <span className='sub-title'>{`${t('FILTER_BY_DATE')}`}</span>
                  <Calendar onChange={onChange} />
                  <button
                    className='button-fifth'
                    type='button'
                    onClick={() => {
                      getDataGraph()
                    }}
                  >
                    {`${t('SEARCH')}`}
                  </button>
                </div>
                <div className='col-12 col-md-8 plan-card'>
                  <ByTimeDashboard
                    data={chartData}
                    type={'url'}
                    dateRange={`${formatDateLong(
                      String(fromDateSelected)
                    )} - ${formatDateLong(String(toDateSelected))}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogModal
          title={`${t('EDIT_LINK')}`}
          open={openEdit}
          handleClose={() => setOpenEdit(false)}
        >
          <div className='d-flex flex-column'>
            <EditLinksForm
              data={dataLink}
              onSubmit={handleSubmitEdit}
              type='link'
              handleClose={handleCloseEdit}
            />
          </div>
        </DialogModal>
      </div>
    </>
  )
}

export default LinkDetail
